<template>
  <SfModalPortal
    modal-id="delete-company-link-modal"
    size="xs"
    @close="onClose"
  >
    <template #title> {{ I18n.t('company_links.destroy.confirm') }} </template>
    <template #body>
      <span v-if="companyLinkToDelete">
        <div class="sf-align-l">
          <div
            v-dompurify-html="
              I18n.t('company_links.destroy.description_header_html', {
                source_company_name: companyLinkToDelete.sourceCompanyName,
              })
            "
            class="sf-below-2"
          ></div>
          <div
            v-dompurify-html="
              I18n.t('company_links.destroy.description_list_html', {
                source_company_name: companyLinkToDelete.sourceCompanyName,
                target_company_name: companyLinkToDelete.targetCompanyName,
              })
            "
            class="sf-delete-company-link__text"
          ></div>
        </div>
      </span>
    </template>
    <template #messages-bottom>
      <SfModalMessage v-if="errorMessage" variant="error">
        {{ errorMessage }}
      </SfModalMessage>
      <SfModalMessage v-else variant="warning">
        {{ I18n.t('company_links.destroy.confirmation_warning') }}
      </SfModalMessage>
    </template>
    <template #footer>
      <div class="tw-w-full">
        <div class="sf-level sf-level-center">
          <SfButtonDs variant="secondary" @click="onClose">
            {{ I18n.t('company_links.destroy.cancel') }}
          </SfButtonDs>
          <SfButtonDs
            variant="danger"
            icon-before="trash-can"
            @click="handleDeleteCompanyLink"
          >
            {{ I18n.t('company_links.destroy.delete_link') }}
          </SfButtonDs>
        </div>
      </div>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import type { Ref } from 'vue'

import { SfLog } from '@app/adapters/logger'
import {
  SfModalPortal,
  SfModalMessage,
} from '@app/shared/vue/components/sf-modal'
import { SfButtonDs } from '@silverfin/ui-components'
import { ref, watch } from 'vue'
import { deleteCompanyLink } from '@app/application/company_links/api'
import { useCompanyLinksStore } from '@app/application/company_links/store/use-company-links'
import { storeToRefs } from 'pinia'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({
  name: 'DeleteCompanyLinkModal',
})

const I18n = useI18n()

const companyLinksStore = useCompanyLinksStore()

const errorMessage: Ref<string> = ref('')

interface Props {
  shouldOpenManageModalOnClose?: boolean
}
const props = defineProps<Props>()

const { companyLinkToDelete } = storeToRefs(companyLinksStore)

async function handleDeleteCompanyLink() {
  try {
    if (!companyLinkToDelete.value) {
      return
    }

    const linkId = companyLinkToDelete.value.id
    await deleteCompanyLink({
      sourceCompanyId: linkId,
    })
    companyLinksStore.removeCompanyLink(linkId)
    onClose()
  } catch (error) {
    errorMessage.value = I18n.t('company_links.destroy.error')
    SfLog.warn('Failed to delete a company link', {
      extra: {
        error: error,
      },
    })
  }
}

watch(
  () => companyLinkToDelete.value,
  (newValue, oldValue) => {
    if (newValue && newValue?.id !== oldValue?.id) {
      window.modalCitizen.openModalPortal('delete-company-link-modal')
    }
  }
)

// Delete Modal can be open in two ways:
// 1. From the manage company links modal. In this case, we should open the manage company links modal on close.
// 2. From the edit client - company links view. In this case, we should simply close the modal on close.
function onClose() {
  companyLinksStore.clearLinkForDeletion()
  if (props.shouldOpenManageModalOnClose) {
    window.modalCitizen.openModalPortal('manage-company-links-modal')
  } else {
    window.modalCitizen.closeModalPortal()
  }
}
</script>

<style lang="scss">
.sf-delete-company-link__text {
  word-break: break-word;

  ul {
    list-style-type: disc;
    margin-left: 20px;
    margin-bottom: 0;
  }
}
</style>
